import d3 from "d3";
import R from "ramda";
import * as core from "../data/core.js";
import { createSelector } from "reselect";
import {compare, ascending, descending} from "../data/compare.js";
import $ from "jquery";
import createPopup from "../page/createPopup.js";

export default function createSesScatter(initialState) {

    const state = {
        selector: "",
        width: 0,
        indicators: {},
        locations: {},
        cleanData: {},
        activeIndicator: null,
        hoveredIndicator: null,
        onIndicatorClick: null
    };

    function setState(newState) {
        Object.assign(state, newState);
        update();
    }

    // merge the initial state in with the default state before we build the svg
    Object.assign(state, initialState);

    const popup = createPopup({
        container: state.selector,
        offset: 0,
        triangle: "top",
        isHidden: true,
        closeBtn: false
    });

    const margin = {
        top: 10,
        right: 10,
        bottom: 10,
        left: 10
      };

    const hoverDuration = 200;

    const container = d3.select(state.selector);

    const minChartWidth = 55;

    var boxWidth, boxHeight;

    function calcBoxWidth(width, charts){
        if(Math.floor(width/charts) < minChartWidth){
            return charts - 1;
        }
        return calcBoxWidth(width, charts + 1);
    }

    function determineChartsPerRow(width){
        return calcBoxWidth(width, 1);
    }

    const spread = x => {
          var extents = d3.extent(x, core.latestYearValue);
          return extents[1]/extents[0];
        },
        spreadComparator = compare(x => spread(x.data), descending),
        valueComparator = compare(core.latestYearValue, descending),
        sortOrderComparator = compare(x => x.ind.order, ascending);

    const transformIndicator = R.curry(function(data, ind) {
        return {
            indId: ind.id,
            ind: ind,
            data: R.values(data[ind.id]).filter(d => !R.isNil(d.years[1].value)).sort(valueComparator)
        };
    });

    // this is an expensive function that has paramters that don't change much, use selectors to memoize it
    const getAllIndicators = createSelector(
        state => state.cleanData,
        state => state.indicators,
        (cleanData, indicators) => {
            return R.compose(
                R.sort(sortOrderComparator),
                R.map(transformIndicator(cleanData)),
                R.values,
                R.filter(core.isNotOverallIndicator)
            )(indicators);
        }
    );

    const isHoveredIndicator = d => d.indId === state.hoveredIndicator,
        getMarginLeft = d => isHoveredIndicator(d)? margin.left - 5 : margin.left,
        getMarginTop = d => isHoveredIndicator(d)? margin.top - 5 : margin.top,
        getMarginRight = d => isHoveredIndicator(d)? margin.right - 5 : margin.right,
        getMarginBottom = d => isHoveredIndicator(d)? margin.bottom - 5 : margin.bottom,
        getChartWidth = d => boxWidth - getMarginLeft(d) - getMarginRight(d),
        getChartHeight = d => boxHeight - getMarginTop(d) - getMarginBottom(d);

    function indicatorProps(indicator) {

      var maxY = d3.max(indicator.data, d => d.years[1].value),
        yScale = d3.scale.linear().range([getChartHeight(indicator), 0]).domain([0, maxY]),
        xScale = d3.scale.ordinal().rangeBands([0, getChartWidth(indicator)]).domain(indicator.data.map(d => d.locId));

      return {
        indId: indicator.indId,
        xScale: xScale,
        yScale: yScale,
        line: d => "M 0 " + getChartHeight(indicator) + " L " + d3.svg.line()
          .y(d => yScale(d.years[1].value))
          .x(d => xScale(d.locId))
          .interpolate("linear")(d).slice(1) + " L " + getChartWidth(indicator) + " " + getChartHeight(indicator) + " Z"
      };
    }


    function update() {

        const chartsPerRow = determineChartsPerRow(state.width);

        boxWidth = Math.floor((state.width - 1)/chartsPerRow);
        boxHeight = Math.floor((state.width - 1)/chartsPerRow);

        var allIndicators = getAllIndicators(state);

        var indicatorMath = R.indexBy(R.prop("indId"), allIndicators.map(indicatorProps));

        var groupedIndicators = R.groupBy(i => i.ind.dimension, allIndicators);

        var groups = container.selectAll("div.group").data(R.keys(groupedIndicators), R.identity);

        var groupsEnter = groups.enter()
            .append("div")
            .attr("class", "group");

        groups.exit().remove();

        groupsEnter.append("h4")
            .text(d => core.dimensionNames[d]);

        var charts = groups.selectAll("div.chart").data(g => groupedIndicators[g], d => d.indId);
        var chartsEnter = charts.enter()
            .append("div")
            .attr("class", "chart")
            .on("mouseenter", function(d){

                // in the center of the thumb
                const point = [
                    $(this).offset().left - $(state.selector).offset().left + $(this).width()/2,
                    $(this).offset().top - $(state.selector).offset().top + $(this).outerHeight()/2
                ];

                // offset it so the pointer is at the bottom or top
                const offset = -1 * $(this).outerHeight()/2;

                showPopUp(state.indicators[d.indId], point, offset);
                setState({hoveredIndicator: d.indId });

            }).on("mouseleave", function(){
                hidePopUp();
                setState({hoveredIndicator: null });
            })
            .on("touchstart", function(d){
                state.onIndicatorClick(d.indId);
            })
            .on("click", function(d){
                state.onIndicatorClick(d.indId);
            });

        // set width/height here (even though we're transitioning on update) so the viz
        // is the correct height when it loads
        // (fixes a bug with the sticky nav)
        chartsEnter.append("svg")
            .style("width", boxWidth + "px")
            .style("height", boxHeight + "px");

        chartsEnter.select("svg").append("g")
            .attr("class", "wrapper");

        charts.exit().remove();

        charts
            .transition().duration(hoverDuration)
            .style("width", boxWidth + "px")
            .style("height", boxHeight + "px");

        charts.select("svg")
            .transition().duration(hoverDuration)
            .attr("width", boxWidth)
            .attr("height", boxHeight);

        charts.select("g.wrapper")
            .transition().duration(hoverDuration)
            .attr("transform", d => `translate(${getMarginLeft(d)},${getMarginTop(d)})`);

        var graphLine = charts.select("g.wrapper").selectAll("path.perf").data(d => [d]);
        graphLine.enter().append("path").attr("class", "perf");
        graphLine.exit().remove();

        graphLine
            .transition().duration(hoverDuration)
            .attr("d", d => indicatorMath[d.indId].line(d.data));

        chartsEnter.select("g.wrapper").append("path")
            .attr("class", "axis")
            .attr("marker-start", "url(#arrow)");

        charts.select("path.axis")
            .transition().duration(hoverDuration)
            .attr("d", d => `M -3, 0 L -3 ${getChartHeight(d)}`);
    }

    update();

    function showPopUp(ind, point, offset){

        const html = $(`
            <div class="spread__popup">
                ${ind.shortName}
            </div>`);

        popup.setState({
            title: "",
            html: html,
            offset,
            top:  point[1],
            left: point[0],
            isHidden: false,
            flipToContain: true
        });
    }

    function hidePopUp(){
        popup.hidePopup();
    }

    // external api
    return {
        setWidth: (width) => setState({ width }),
        setActiveIndicator: (ind) => setState({activeIndicator: ind})
    };
}
